import { StoreLocatorInfoBox } from '@/components/content/StoreLocator/parts/InfoBox';
import { StoreLocatorMarker } from '@/components/content/StoreLocator/parts/Marker';
import { StoreLocatorSideList } from '@/components/content/StoreLocator/parts/SideList';
import { storeLocatorMapContainerSX } from '@/components/content/StoreLocator/styles/mapContainer';
import { useStoreLocator } from '@/data/Content/StoreLocator';
import { GOOGLE_MAP_ZOOM } from '@/data/constants/storeLocator';
import { ContentProvider } from '@/data/context/content';
import { ID } from '@/data/types/Basic';
import { Box, Grid, Skeleton, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DirectionsRenderer, DirectionsService, GoogleMap } from '@react-google-maps/api';
import React, { FC } from 'react';
import { combineSX } from '@/utils/combineSX';
import { useIsMobile } from '@/data/hooks/useIsMobile';
import { ContentRecommendation } from '@/components/content/ContentRecommendation';

export const StoreLocator: FC<{ id: ID }> = () => {
	const { breakpoints } = useTheme();
	const isMobile = useIsMobile();
	const isDesktop = useMediaQuery(breakpoints.up('md'));

	const {
		isLoaded,
		locator,
		showDirection,
		destination,
		mapCenter,
		directionResults,
		searchTextFieldRef,
		currentLocation,
		clickedIndex,
		searchTerm,
		clearSearch,
		mapOnLoad,
		directionsCallback,
		findNearStore,
		onPlaceChanged,
		searchBoxOnLoad,
		onListItemClick,
		...rest
	} = useStoreLocator();

	return (
		<React.Fragment>
			<ContentProvider
				value={{
					locator,
					showDirection,
					mapCenter,
					clickedIndex,
					searchTerm,
					searchTextFieldRef,
					currentLocation,
					findNearStore,
					searchBoxOnLoad,
					onPlaceChanged,
					onListItemClick,
					clearSearch,
					...rest,
				}}
			>
				<Grid
					container
					justifyContent="center"
					alignItems="stretch"
					sx={isDesktop ? storeLocatorMapContainerSX : null}
				>
					<Grid
						item
						xs
						md={4}
						sx={isDesktop ? combineSX([storeLocatorMapContainerSX, { paddingTop: '0' }]) : null}
					>
						<StoreLocatorSideList isLoaded={isLoaded} />
					</Grid>
					{!isMobile ? (
						<Grid
							container
							item
							xs={12}
							md={8}
							direction="column"
							justifyContent="center"
							alignItems="stretch"
							sx={storeLocatorMapContainerSX}
						>
							<Grid item xs position="relative">
								{!isLoaded ? (
									<Skeleton variant="rectangular" width={'100%'} height={'100%'} />
								) : (
									<>
										<GoogleMap
											mapContainerStyle={{ height: '100%', width: '100%', padding: 0, margin: 0 }}
											zoom={GOOGLE_MAP_ZOOM.INIT}
											center={mapCenter}
											onLoad={mapOnLoad}
										>
											{!showDirection
												? locator.storeList.map((store, index) => (
														<StoreLocatorMarker key={store.id} store={store} index={index} />
												  ))
												: null}
											{showDirection && destination ? (
												<DirectionsService
													options={{
														destination,
														origin: mapCenter,
														travelMode: google.maps.TravelMode.DRIVING,
														provideRouteAlternatives: true,
													}}
													callback={directionsCallback}
												/>
											) : null}
											{showDirection ? (
												<DirectionsRenderer
													options={{
														directions: directionResults,
														suppressInfoWindows: true,
													}}
												/>
											) : null}
										</GoogleMap>
										<StoreLocatorInfoBox />
									</>
								)}
							</Grid>
						</Grid>
					) : null}
				</Grid>
			</ContentProvider>
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
				}}
			>
				<ContentRecommendation
					id={`DPV2_EncuentraTuTienda_SEO`}
					properties={{
						emsName: `DPV2_EncuentraTuTienda_SEO`,
					}}
					loadingElement={
						<Grid container spacing={1}>
							<Grid item md={6} xs={12}>
								<Skeleton variant="rounded" width={'100%'} height={50} />
								<Skeleton variant="rounded" width={'100%'} height={'215px'} sx={{ mt: '15px' }} />
							</Grid>
							<Grid item md={6} xs={12}>
								<Skeleton variant="rounded" width={'100%'} height={'200px'} />
							</Grid>
						</Grid>
					}
				/>
			</Box>
		</React.Fragment>
	);
};
